import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { addDays, format, parseISO, startOfDay, subMonths } from 'date-fns';
import {
  ConfigBaseFilter,
  ListLayout,
  ListLayoutMultipleComponent,
  SimpleConfigBaseFilter,
  SkeletonLoaderLayout
} from 'gung-list';
import { BehaviorSubject, catchError, map, Observable, of, switchMap } from 'rxjs';
import { OpenOrderTableComponent } from '../../components/open-order-table/open-order-table.component';
import { Order } from '../../models/order';
import { CustomerService } from '../customers/customer.service';
import { MetadataService } from '../metadata/metadata.service';
import { OrderListConfigService } from '../order-list-config/order-list-config.service';

@Injectable({
  providedIn: 'root'
})
export class OpenOrdersConfigService extends OrderListConfigService {
  protected translationService = inject(TranslateService);
  protected metadataService = inject(MetadataService);
  protected customerService = inject(CustomerService);

  topFilter = true;
  skeletonLoaderLayout: SkeletonLoaderLayout = 'table';

  protected minDateSubject = new BehaviorSubject<string | undefined>(this.getDefaultMinDate());

  getDefaultMonthsBack(): number | undefined {
    return undefined;
  }

  getDefaultMinDate(): string | undefined {
    const monthsBack = this.getDefaultMonthsBack();
    if (monthsBack == null) {
      return undefined;
    }
    return format(subMonths(new Date(), monthsBack), 'yyyy-MM-dd');
  }

  getMinDate(): string | undefined {
    return this.minDateSubject.getValue();
  }

  setMinDate(minDate: string): void {
    this.minDateSubject.next(minDate);
  }

  getLayouts(): ListLayout<Order>[] {
    return [
      {
        getIconClass: () => '',
        getListItemComponent: () => OpenOrderTableComponent,
        getListLayoutComponent: () => ListLayoutMultipleComponent,
        getName: () => 'Order list'
      }
    ];
  }

  getFilters(): ConfigBaseFilter<Order>[] {
    return [new OrderStatusFilter(), new OrderDateFilter(), new OrderDeliveryDateFilter()];
  }

  getSearchTerms(item: Order): string[] {
    const result = super.getSearchTerms(item);

    if (item.extra?.customerName) {
      result.push(item.extra.customerName);
    }

    return result;
  }

  protected fetchOpenOrders(): Observable<Order[]> {
    return this.minDateSubject.pipe(
      switchMap(minDate => {
        if (!minDate) {
          // No date window in use: let errors propagate as before so filter-list can show its
          // error panel. Nothing will re-emit here, so completing the stream costs nothing.
          return this.orderService.getOpenOrders();
        }
        // toDate is exclusive on the backend, so tomorrow to include today
        return (
          this.orderService
            .getOpenOrders(false, undefined, parseISO(minDate), addDays(startOfDay(new Date()), 1))
            // Swallow per request: filter-list wraps getItems() in its own catchError, which would
            // complete this stream on the first failure and ignore every later date change.
            .pipe(catchError(() => of([])))
        );
      })
    );
  }

  getItems() {
    return this.fetchOpenOrders().pipe(
      // Drop entries with a falsy id so the list doesn't break filtering. The backend can
      // emit proxy rows (e.g. DELIVERED_ORDER_PROXY, INVOICED_ORDER_PROXY) with empty ids;
      // filter-list.service throws if getItemId returns falsy. If an order is missing from
      // the list during troubleshooting, check the open-orders payload for items with no id.
      map(orders => {
        const result = orders.filter(order => !!order.id);
        // orderMapper already created new objects — mutate extra in-place to avoid a second
        // full spread over potentially thousands of large order objects.
        for (const order of result) {
          if (order.extra) {
            order.extra.customerName = order.extra.deliveryCustomer?.name ?? '';
          }
        }
        return result;
      })
    );
  }

  getAddQueryParameterLimitToUrl(): boolean {
    return true;
  }
}

class OrderStatusFilter extends SimpleConfigBaseFilter<Order> {
  getName(): string {
    return 'ORDER_STATUS';
  }

  getOptionIds(item: Order): string[] {
    return [item.extra?.gme?.status];
  }

  getOptionName(key: string): string {
    return key;
  }
}

export class OrderDateFilter extends SimpleConfigBaseFilter<Order> {
  type = 'dateRangeFilter';

  getName(): string {
    return 'ORDER_DATE';
  }

  getOptionIds(item: Order): string[] {
    if (item?.extra?.gme?.orderDate) {
      return [format(new Date(item?.extra?.gme?.orderDate), 'yyMMdd')];
    }
    return [];
  }

  getOptionName(key: string): string {
    return key;
  }
}

export class OrderDeliveryDateFilter extends SimpleConfigBaseFilter<Order> {
  type = 'dateRangeFilter';

  getName(): string {
    return 'DELIVERY_DATE';
  }

  getOptionIds(item: Order): string[] {
    if (item?.extra?.gme?.deliveryDate) {
      return [format(new Date(item?.extra?.gme?.deliveryDate), 'yyMMdd')];
    }
    return [];
  }

  getOptionName(key: string): string {
    return key;
  }
}
